import { DefaultAccordion } from '../../generic/DefaultAccordion'
import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useEffect, useState } from 'react'
import { useSmartInterval } from '../../generic/hooks/useSmartInterval'
import { Button, CircularProgress, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import SmartGroupCreator from '../../generic/smartSortable/SmartGroupCreator'
import useGetExtendedColumns from '../../generic/hooks/useGetExtendedColumns/useGetExtendedColumns'

const Features = (props) => {
  const { t_ } = useSmartTranslation()
  const [newValueTyping, setNewValueTyping] = useState({ code: true, name: true })
  const [openFeatureGroupsDialog, setOpenFeatureGroupsDialog] = useState(false)
  const [initialGroups, setInitialGroups] = useState(null)
  const [userFeatureGroups, setUserFeatureGroups] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [featureTypes, setFeatureTypes] = useState([])
  const extendedColumns = useGetExtendedColumns('feature')
  useEffect(() => {
    const loadFeatureTypes = async () => {
      setFeatureTypes(await apiGet('featureTypes'))
    }
    loadFeatureTypes()
  }, [])

  const condition = (value, field) => {
    let errorMsg = ''

    if (userFeatureGroups !== null) {
      if ((value === '' || value === null) && !newValueTyping[field]) {
        return t_('Required *')
      }

      if (value !== '' && newValueTyping[field]) {
        const copy = newValueTyping
        copy[field] = false
        setNewValueTyping(copy)
      }

      const x = userFeatureGroups.ungrouped.filter(feature => feature[field] === value)
      if (x.length !== 0) {
        errorMsg = t_('Key duplicated')
      }

      userFeatureGroups.grouped.forEach(featureGroup => {
        const y = featureGroup.features.filter(feature => feature[field] === value)
        if (y.length !== 0) {
          errorMsg = t_('Key duplicated')
        }
      })
    }

    return errorMsg
  }

  const columns = [
    {
      name: t_('Code'),
      field: 'code',
      readOnly: true,
      charLimit: 60,
      mandatory: true,
      unique: true,
      condition: (rowData) => {
        return condition(rowData.code, 'code')
      }
    },
    {
      name: t_('Name'),
      field: 'name',
      charLimit: 80,
      mandatory: true,
      unique: true
    },
    {
      name: t_('Description'),
      field: 'description',
      charLimit: 200
    },
    {
      name: t_('Feature type'),
      field: 'featureType.name',
      type: 'smartSelect',
      defaultNewValue: featureTypes.find((item) => item.code === 'QUANTITATIVE'),
      readOnly: (row) => {
        return row.hasAssignsWithValues
      },
      edit: {
        source: async () => {
          return featureTypes
        },
        id: 'id',
        field: 'name'
      },
      mandatory: true
    }
  ]
  const getColumns = () => {
    if (!Array.isArray(extendedColumns) || extendedColumns.length === 0) return columns
    return columns.concat(extendedColumns)
  }
  const actions = {
    edit: {
      fetchFunction: (row) => {
        return apiPut('feature', row)
      }
    },
    create: {
      fetchFunction: (row) => {
        return apiPost('feature', row)
      }
    },
    delete: {
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('feature/' + rows[0].id, rows)
        } else {
          return apiDelete('features', rows)
        }
      }
    }
  }

  const fetchData = async () => {
    const featureList = await apiGet('features/noPagination')
    const features = []
    const featureGroups = []
    featureList.forEach((feature) => {
      if (feature.featureGroup !== null) {
        let featureGroup = featureGroups.find((group) => group.id === feature.featureGroup.id)
        if (featureGroup === undefined) {
          featureGroup = {
            id: feature.featureGroup.id,
            name: feature.featureGroup.name,
            features: []
          }
          featureGroups.push(featureGroup)
        }
        featureGroup.features.push(feature)
        return
      }
      features.push(feature)
    })

    return {
      grouped: featureGroups,
      ungrouped: features
    }
  }

  const keys = {
    itemId: 'id',
    itemText: 'name',
    groupId: 'id',
    groupText: 'name',
    groupItems: 'features'
  }

  const updateInitialGroups = (newGroups) => {
    setInitialGroups(newGroups)
  }

  const setIntervalData = (data) => {
    setUserFeatureGroups(data)
  }

  useSmartInterval(fetchData, 1000, setIntervalData)

  const handleSaveFeatureGroups = async (newGroups) => {
    let hasEnoughFeatures = true
    newGroups.grouped.forEach((group) => {
      if (group.features.length < 2) {
        hasEnoughFeatures = false
        setErrorMessage(t_('Group must have at least 2 features'))
      }
    })
    if (hasEnoughFeatures) {
      const updatedGroupsGrouped = newGroups.grouped.map((group) => ({
        ...group
      }))

      await apiPut('featureGroups', updatedGroupsGrouped)
      await apiPut('features', newGroups.ungrouped)

      setOpenFeatureGroupsDialog(false)
      setErrorMessage(null)
      updateInitialGroups(await fetchData())
    }
  }

  const renderGroupedFeatures = (groupedFeatures) => {
    return groupedFeatures.map((group) => {
      const actionGroup = {
        edit: {
          fetchFunction: (row) => {
            return apiPut('feature', row)
          }
        },
        create: {
          fetchFunction: (row) => {
            const createdRow = { ...row, featureGroup: { id: group.id, name: group.name } }
            return apiPost('feature', createdRow)
          }
        },
        delete: {
          fetchFunction: (rows) => {
            if (rows.length === 1) {
              return apiDelete('feature/' + rows[0].id)
            } else {
              return apiDelete('features', rows)
            }
          }
        }
      }

      const headerTitle = (group) => {
        return <Typography>{group.name}</Typography>
      }

      return (
        <DefaultAccordion
          headerRender={headerTitle(group)}
          key={'feature-group-' + group.name}
          defaultExpanded={false}
        >
          <SmartMateriaUITable
            tableTuneUniqueKey='features'
            columns={getColumns()}
            dataFetch={group.features}
            safeDeleteShowProperty='name'
            pageable={false}
            actions={actionGroup}
            onEditIndexChanged={(index) => {
              if (index >= -1) {
                setNewValueTyping({ name: true, code: true })
              }
            }}
          />
        </DefaultAccordion>

      )
    })
  }

  const defaultToolbar = {
    renderSticky: {
      fetchFunction: () => {
        return (
          <Button color='primary' variant='text' onClick={() => setOpenFeatureGroupsDialog(true)}>{t_('Group')}</Button>

        )
      }
    }
  }

  if (extendedColumns === null || extendedColumns === undefined) {
    return <CircularProgress />
  }

  return (
    <>
      <SmartDialog
        setOpen={openFeatureGroupsDialog}
        close={false}
        accept
        cancel
        cancelCallback={() => { setOpenFeatureGroupsDialog(false); setErrorMessage(null) }}
        acceptCallback={() => handleSaveFeatureGroups(initialGroups)}
        title={{ render: <Typography variant='h5'> {t_('Group')} / {t_('Ungroup')}</Typography> }}
        renderComponent={
          <SmartGroupCreator
            onChange={setInitialGroups}
            grouped={userFeatureGroups?.grouped || []}
            ungrouped={userFeatureGroups?.ungrouped || []}
            keys={keys}
            errorMessage={errorMessage}
          />
        }
      />
      <SmartMateriaUITable
        tableTuneUniqueKey='features'
        columns={getColumns()}
        title={t_('Features')}
        dataFetch={userFeatureGroups?.ungrouped || []}
        safeDeleteShowProperty='name'
        actions={actions}
        pageable={false}
        multipleSelection
        toolbar={defaultToolbar}
      />
      {userFeatureGroups?.grouped !== null && userFeatureGroups?.grouped?.length > 0
        ? renderGroupedFeatures(userFeatureGroups.grouped)
        : null}

    </>
  )
}

export default Features
